import { CATEGORY_DISPLAY_NAME_SPLIT_CHAR } from '@/constants/assetList';
import { OfficialResourceAPI } from '@/server/AssetManage';
import { OfficialResourceCategoryItem } from '@/type/assetList';

import { isNil } from '@/utils';
import {
  CreateOfficialResourceCategoryDto,
  UpdateOfficialResourceCategoryDto,
} from '@easytwin/types';
import { message } from '@easyv/react-components';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { useMemo } from 'react';

export function useOfficialResourceCategoryApi() {
  const {
    data,
    loading: isLoading,
    run: refetchOfficialResourceCategories,
  } = useRequest(
    async () => {
      const category = await OfficialResourceAPI.getOfficialResourceCategory();
      const data = category.sort((a, b) => {
        return dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix();
      });

      return data;
    },
    { cacheKey: 'official categories', debounceWait: 500, debounceTrailing: true },
  );

  const [officialCategoriesTreeList, officialCategoriesMap] = useMemo(() => {
    if (data === undefined) return [];
    const map = new Map<number, OfficialResourceCategoryItem>();
    for (const node of data) {
      const item: OfficialResourceCategoryItem = {
        ...node,
        unclassified: false,
        children: [],
        displayName: '',
      };
      map.set(node.id, item);
    }

    const root: OfficialResourceCategoryItem[] = [];
    for (const newNode of map.values()) {
      const parentId = newNode.parentId;
      if (isNil(parentId) || parentId === -1) {
        root.push(newNode);
      } else {
        const parent = map.get(parentId);
        parent && parent.children.push(newNode);
      }
    }

    for (const newNode of map.values()) {
      let displayName = newNode.name;

      let p = newNode.parentId ? map.get(newNode.parentId) : null;
      while (p) {
        displayName = p.name + ` ${CATEGORY_DISPLAY_NAME_SPLIT_CHAR} ` + displayName;
        p = p.parentId ? map.get(p.parentId) : null;
      }
      newNode.displayName = displayName;
    }

    return [root, map];
  }, [data]);

  const { run: createOfficialResourceCategory } = useRequest(
    (dto: CreateOfficialResourceCategoryDto) => {
      return OfficialResourceAPI.createOfficialResourceCategory(dto);
    },
    {
      manual: true,
      onSuccess: (data, param) => {
        message.success('创建成功');
        refetchOfficialResourceCategories();
      },
      onError: (error, param) => {
        message.error(`创建失败：${error.message}`);
      },
    },
  );

  const { run: deleteOfficialResourceCategory } = useRequest(
    (ids: number[]) => {
      return OfficialResourceAPI.deleteOfficialResourceCategory(ids);
    },
    {
      manual: true,
      onSuccess: (data, param) => {
        message.success('删除成功');
        refetchOfficialResourceCategories();
      },
      onError: (error, param) => {
        message.error(`删除失败：${error.message}`);
      },
    },
  );

  const { run: updateOfficialResourceCategory } = useRequest(
    (id: number, dto: UpdateOfficialResourceCategoryDto) => {
      return OfficialResourceAPI.updateOfficialResourceCategory(id, dto);
    },
    {
      manual: true,
      onSuccess: (data, param) => {
        message.success('更新成功');
        refetchOfficialResourceCategories();
      },
      onError: (error, param) => {
        message.error(`更新失败：${error.message}`);
      },
    },
  );

  return {
    data,
    officialCategoriesTreeList,
    officialCategoriesMap,
    createOfficialResourceCategory,
    deleteOfficialResourceCategory,
    updateOfficialResourceCategory,
  };
}
